import { type ReactNode } from 'react';
import { Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { cn } from './cn';

export type KpiVariant = 'neutral' | 'positive' | 'warning' | 'danger' | 'accent';
export type KpiTrend = 'positive' | 'negative' | 'neutral';

export interface KpiCardProps {
  label: string;
  value: ReactNode;
  delta?: string;
  trend?: KpiTrend;
  caption?: string;
  icon?: ReactNode;
  variant?: KpiVariant;
  onClick?: () => void;
  className?: string;
}

const ACCENT_BAR: Record<KpiVariant, string> = {
  neutral: 'bg-border-default',
  positive: 'bg-state-success',
  warning: 'bg-state-warning',
  danger: 'bg-state-danger',
  accent: 'bg-accent',
};

const TREND_COLOR: Record<KpiTrend, string> = {
  positive: 'text-state-success',
  negative: 'text-state-danger',
  neutral: 'text-text-tertiary',
};

function TrendIcon({ trend }: { trend: KpiTrend }) {
  const Cmp = trend === 'positive' ? TrendingUp : trend === 'negative' ? TrendingDown : Minus;
  return <Cmp className="h-3 w-3" aria-hidden />;
}

export function KpiCard({ label, value, delta, trend = 'neutral', caption, icon, variant = 'neutral', onClick, className }: KpiCardProps) {
  const Tag = onClick ? 'button' : 'div';

  return (
    <Tag
      onClick={onClick}
      className={cn(
        'relative flex flex-col gap-2 overflow-hidden rounded-lg border border-border-subtle bg-surface-1 p-4 text-left',
        onClick &&
          'cursor-pointer transition-colors duration-[var(--duration-fast)] ease-[var(--ease-standard)] hover:bg-surface-2 hover:border-border-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus',
        className,
      )}
      aria-label={typeof value === 'string' || typeof value === 'number' ? `${label}: ${value}` : label}
    >
      <span aria-hidden className={cn('absolute left-0 top-0 h-full w-0.5', ACCENT_BAR[variant])} />

      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-text-secondary">
        {icon && <span className="inline-flex shrink-0 text-text-tertiary">{icon}</span>}
        <span>{label}</span>
      </div>

      <div className="flex items-end justify-between gap-3">
        <div className="text-3xl font-semibold leading-none text-text-primary tabular-nums">{value}</div>
      </div>

      {(delta || caption) && (
        <div className="flex items-center gap-1.5 text-xs tabular-nums">
          {delta && (
            <span className={cn('inline-flex items-center gap-0.5 font-medium', TREND_COLOR[trend])}>
              <TrendIcon trend={trend} />
              {delta}
            </span>
          )}
          {caption && <span className="text-text-tertiary">{caption}</span>}
        </div>
      )}
    </Tag>
  );
}
